import * as d3 from 'd3';
import {geoAlbers, geoPath} from 'd3-geo';
import type * as geojson from 'geojson';
import React, {useEffect, useRef} from 'react';

const MAP_WIDTH = 180;
const MAP_HEIGHT = 120;

type SiteMarker = {
  siteId: string;
  name: string;
  lat: number | null;
  lon: number | null;
};

type Props = {
  sites: SiteMarker[];
  activeSiteId: string;
  basinOutline?: geojson.Geometry | null;
};

/**
 * Compact locator map for a single site section. Renders the full basin
 * outline (so readers orient relative to the Columbia extent shown in the
 * Exec Summary map), with the active site highlighted and the rest muted to
 * tiny gray dots.
 */
export default function SiteLocatorMap({sites, activeSiteId, basinOutline}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !basinOutline) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`);

    const feat: geojson.Feature = {type: 'Feature', geometry: basinOutline, properties: {}};
    const projection = geoAlbers()
      .rotate([120, 0])
      .parallels([43, 48])
      .fitSize([MAP_WIDTH - 8, MAP_HEIGHT - 8], feat as any);
    const path = geoPath().projection(projection);

    svg
      .append('path')
      .attr('d', path(feat as any) ?? '')
      .attr('fill', '#e0f2fe')
      .attr('stroke', '#0369a1')
      .attr('stroke-width', 0.6)
      .attr('fill-opacity', 0.6);

    const activeSite = sites.find((s) => s.siteId === activeSiteId);
    const otherSites = sites.filter(
      (s) => s.siteId !== activeSiteId && s.lat != null && s.lon != null
    );

    for (const s of otherSites) {
      const coords = projection([s.lon!, s.lat!]);
      if (!coords) continue;
      svg
        .append('circle')
        .attr('cx', coords[0])
        .attr('cy', coords[1])
        .attr('r', 2)
        .attr('fill', '#9ca3af')
        .attr('stroke', '#fff')
        .attr('stroke-width', 0.5);
    }

    if (activeSite && activeSite.lat != null && activeSite.lon != null) {
      const coords = projection([activeSite.lon, activeSite.lat]);
      if (coords) {
        svg
          .append('circle')
          .attr('cx', coords[0])
          .attr('cy', coords[1])
          .attr('r', 7)
          .attr('fill', 'none')
          .attr('stroke', '#dc2626')
          .attr('stroke-width', 1.2)
          .attr('opacity', 0.6);
        svg
          .append('circle')
          .attr('cx', coords[0])
          .attr('cy', coords[1])
          .attr('r', 3.5)
          .attr('fill', '#dc2626')
          .attr('stroke', '#fff')
          .attr('stroke-width', 1);
      }
    }
  }, [sites, activeSiteId, basinOutline]);

  if (!basinOutline) return null;

  return <svg ref={svgRef} width={MAP_WIDTH} height={MAP_HEIGHT} className="shrink-0" />;
}
